/*
 * @Author: Ryan
 * @Date: 2026-01-14
 * @Description: AI Video Generator features section showcasing key features with alternating layout
 */
import Image from 'next/image';
import React from 'react';

import StartButton from './StartButton';

interface FeatureItemProps {
  title: string;
  description: string;
  ctaText: string;
  imageUrl: string;
  imageAlt: string;
  reverse?: boolean;
}

const FeatureItem: React.FC<FeatureItemProps> = ({
  title,
  description,
  ctaText,
  imageUrl,
  imageAlt,
  reverse = false,
}) => {
  return (
    <div
      className={`flex flex-col ${reverse ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-6 pad:gap-8 sm:gap-12`}
    >
      {/* Image */}
      <div className="flex-1 w-full">
        <div className="rounded-2xl overflow-hidden">
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={540}
            height={360}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full flex flex-col items-start">
        <h3 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mb-3 pad:mb-4">
          {title}
        </h3>
        <p className="text-sm pad:text-base sm:text-base text-gray-600 mb-4 pad:mb-6 leading-relaxed">
          {description}
        </p>
        <StartButton text={ctaText} />
      </div>
    </div>
  );
};

const PowerfulFeaturesSection: React.FC = () => {
  const features = [
    {
      title: 'Turn Simple Text Into Video',
      description:
        'Type a short description or script, and FalcoCut text-to-video tool transforms it into a fully animated video. The AI interprets your idea, builds the setting, and adds motion that matches your story. It works even if your prompt is only a few words, making it friendly for beginners. You can use it for storytelling, ads, YouTube content, tutorials, or quick concept previews without needing editing experience.',
      ctaText: 'Turn Text to Video Now',
      imageAlt: 'Turn simple text into video',
      imageUrl:
        'https://assets.falcocut.ai/ai-video-generator/feature-text-to-video.webp',
    },
    {
      title: 'Animate Image with Natural Motion',
      description:
        "Upload a photo, illustration, or product shot and watch it come alive with smooth, realistic movement. The AI image-to-video converter adds depth, motion, and transitions while keeping the original style intact. It's ideal for turning static visuals into dynamic clips for marketing, design, or creative storytelling. You can animate portraits, product images, or artwork to produce engaging videos in just a few steps.",
      ctaText: 'Turn Image to Video Now',
      imageUrl:
        'https://assets.falcocut.ai/ai-video-generator/feature-image-to-video.webp',
      imageAlt: 'Animate image with natural motion',
    },
    {
      title: 'Consistent Characters Across Scenes',
      description:
        "Our image asset library lets you store and reuse characters you've created across multiple videos. The AI maintains their appearance, style, and proportions from scene to scene, giving your content a consistent look. This prevents sudden changes in facial features or clothing between shots. It's especially helpful for creators building stories, branded series, or recurring characters.",
      ctaText: 'Create Your Video',
      imageUrl:
        'https://assets.falcocut.ai/ai-video-generator/feature-consistent-characters.webp',
      imageAlt: 'Consistent characters across scenes',
    },
    {
      title: 'Choose From Various AI Models',
      description:
        'FalcoCut offers multiple AI models—Sora, Veo, and Kling—so you can choose the one that best fits your story or brand. Each model handles movement, pacing, and style differently, giving you a wide range of creative possibilities. You can describe simple actions like "slow pan," "zoom in," or "follow the character", and the AI interprets them naturally. With these options, your video can match the exact tone and visual style you want without relying on complex editing skills.',
      ctaText: 'Create Your Video',
      imageUrl: 'https://assets.falcocut.ai/ai-video-generator/feature-ai-models.webp',
      imageAlt: 'Choose from various AI models',
    },
    {
      title: 'Auto Audio Generation',
      description:
        'Sound adds emotion, clarity, and realism—and FalcoCut makes it automatic. The AI creates voice and ambient audio that match the moment you describe, so a simple line like "The mom says Happy birthday to her daughter" becomes a warm, natural scene. The sound blends with the visuals to add depth and feeling without extra recording or editing. Your videos come out expressive, complete, and ready to share right away.',
      ctaText: 'Create Your Video',
      imageUrl: 'https://assets.falcocut.ai/ai-video-generator/feature-voice.webp',
      imageAlt: 'Auto audio generation',
    },
    {
      title: 'Photo to Talking Clip',
      description:
        'Turning a photo into a short talking moment makes messages feel more personal and direct for any audience. Simple spoken lines help explain ideas in a clear and friendly way. Visual movement draws attention and keeps viewers focused on the key point. This approach removes the need for filming, making quick updates easy for busy teams. It offers a fast way to share information with a human touch.',
      ctaText: 'Create Your Video',
      imageUrl: 'https://assets.falcocut.ai/ai-video-generator/feature-avatars.webp',
      imageAlt: 'Photo to talking clip',
    },
    {
      title: 'Generate Videos for Any Platform',
      description:
        'Short clips like 4‑second highlights work well for fast‑scrolling platforms where quick impact matters. Longer 12‑second segments give more room for product demos, story beats, or educational moments. Our video creation tool provides proper video output settings to keeps your visuals sharp on TikTok, Instagram, YouTube, and other channels. Adjust duration and resolution to fit any platform in just one click. Now, everything comes out ready to post.',
      ctaText: 'Create Your Video',
      imageUrl: 'https://assets.falcocut.ai/ai-video-generator/feature-export.webp',
      imageAlt: 'Generate videos for any platform',
    },
    {
      title: 'Create in a Connected Workspace',
      description:
        'FalcoCut gives you a flexible creative workspace where you can continue refining your project even after the first video is generated. All your images, clips, voices, and avatars are stored in an organized asset library, so adding new elements or adjusting visuals feels simple and intuitive. You can move between tools to translate clips, add your own voice to videos, or edit your footage without breaking your flow. Everything stays connected so your workflow remains smooth and flexible from start to finish.',
      ctaText: 'Create Your Video',
      imageUrl: 'https://assets.falcocut.ai/ai-video-generator/feature-workspace.webp',
      imageAlt: 'Create in a connected workspace',
    },
  ];

  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3 pad:mb-4">
          Key Features for Fast Video Creation
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base text-center text-gray-600 max-w-4xl mx-auto mb-6 pad:mb-8">
          Generate videos in a way that fits your process, from simple text prompts to
          detailed visual references or character‑driven scenes. Every feature supports
          fast, intuitive creation so you can focus on storytelling instead of technical
          steps.
        </p>

        <div className="space-y-12 pad:space-y-16 sm:space-y-24">
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              title={feature.title}
              description={feature.description}
              ctaText={feature.ctaText}
              imageUrl={feature.imageUrl}
              imageAlt={feature.imageAlt}
              reverse={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PowerfulFeaturesSection;
